import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Restaurants from '../assets/Restaurants';
import './Map.css';
import { useMetaTags } from '../hooks/useMetaTags';

function Map() {
  useMetaTags({
    title: 'Restaurant Map - Find Best Deals in Dubai | DXB Dates',
    description: 'Explore restaurants with 2 for 1 deals on our interactive map. Find dining deals near you in Dubai.',
    ogTitle: 'DXB Dates - Restaurant Map',
    ogImage: 'https://dxbdates.com/og-image.jpg'
  });

  const [mapLoaded, setMapLoaded] = useState(false);

  // Function to load the Google Maps script
  const loadGoogleMapsScript = () => {
    // Check if the script is already loaded
    if (window.google && window.google.maps) {
      initMap();
      return;
    }

    // Create the script element
    const googleMapsScript = document.createElement('script');
    googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDetcC1Q8EJPReuYf-EqBIVnh5LC7S4hBA&callback=initMap`;
    googleMapsScript.async = true;
    googleMapsScript.defer = true;
    
    // Define the callback function
    window.initMap = () => {
      initMap();
    };
    
    // Append the script to the document
    document.head.appendChild(googleMapsScript);
  };

  // Function to initialize the map
  const initMap = () => {
    // Create a map centered on Dubai
    const map = new window.google.maps.Map(document.getElementById('map'), {
      center: { lat: 25.2048, lng: 55.2708 }, // Dubai coordinates
      zoom: 12,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi.park",
          elementType: "geometry",
          stylers: [{ color: "#263c3f" }],
        },
        {
          featureType: "poi.park",
          elementType: "labels.text.fill",
          stylers: [{ color: "#6b9a76" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#38414e" }],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [{ color: "#212a37" }],
        },
        {
          featureType: "road",
          elementType: "labels.text.fill",
          stylers: [{ color: "#9ca5b3" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#746855" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry.stroke",
          stylers: [{ color: "#1f2835" }],
        },
        {
          featureType: "road.highway",
          elementType: "labels.text.fill",
          stylers: [{ color: "#f3d19c" }],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [{ color: "#2f3948" }],
        },
        {
          featureType: "transit.station",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#17263c" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.fill",
          stylers: [{ color: "#515c6d" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#17263c" }],
        },
      ],
    });

    // Create an info window to share between markers
    const infoWindow = new window.google.maps.InfoWindow();

    // Add markers for each restaurant
    const uniqueRestaurants = [];
    const seenNames = new Set();

    // Filter out duplicate restaurants based on name
    Restaurants.forEach(restaurant => {
      if (!seenNames.has(restaurant.name)) {
        seenNames.add(restaurant.name);
        uniqueRestaurants.push(restaurant);
      }
    });

    // Add markers for each unique restaurant
    uniqueRestaurants.forEach((restaurant, index) => {
      if (restaurant.location && restaurant.location.coordinates) {
        const lat = parseFloat(restaurant.location.coordinates.lat.$numberDouble);
        const lng = parseFloat(restaurant.location.coordinates.lng.$numberDouble);

        // Only add marker if we have valid coordinates
        if (lat && lng) {
          const position = { lat: lat, lng: lng };

          // Create a marker
          const marker = new window.google.maps.Marker({
            position: position,
            map: map,
            title: restaurant.name,
            animation: window.google.maps.Animation.DROP,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: '#E74C3C',
              fillOpacity: 1,
              strokeWeight: 0,
              scale: 10
            }
          });

          // Create the content for the info window
          const contentString = `
            <div class="info-window">
              <h3>${restaurant.name}</h3>
              <p>${restaurant.description}</p>
              <p><strong>Cuisine:</strong> ${restaurant.cuisine}</p>
              <p><strong>Price:</strong> ${restaurant.price.priceRange} (${restaurant.price.priceForTwo} for two)</p>
              <p><strong>Rating:</strong> ${restaurant.rating.$numberDouble} ★</p>
              <p><strong>Address:</strong> ${restaurant.location.address}</p>
              <a href="/restaurant/${index}" class="info-window-link">View Details</a>
            </div>
          `;

          // Add click event to the marker
          marker.addListener('click', () => {
            infoWindow.setContent(contentString);
            infoWindow.open(map, marker);
          });
        }
      }
    });

    setMapLoaded(true);
  };

  useEffect(() => {
    loadGoogleMapsScript();
    
    // Clean up function
    return () => {
      // Remove the callback function from the window object
      delete window.initMap;
    };
  }, []);

  return (
    <div className="map-container">
      <div className="map-header">
        <h1>Restaurant Map</h1>
        <p>Explore all our recommended restaurants across Dubai</p>
        <Link to="/" className="back-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
          </svg>
          Back to Home
        </Link>
      </div>
      
      <div id="map" className="map"></div>
      
      {!mapLoaded && (
        <div className="map-loading">
          <div className="spinner"></div>
          <p>Loading map...</p>
        </div>
      )}
      
      <div className="map-footer">
        <p>Click on a marker to see restaurant details</p>
      </div>
    </div>
  );
}

export default Map;
